import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import AvatarEditor from 'react-avatar-editor';
import Message from './Message';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const UploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  width: 100%;
  padding: 20px;
`;

const EditorContainer = styled.div`
  width: 200px;
  height: 199px;
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme }) => theme.inputBg || theme.cardBg};
  position: relative;
  margin-bottom: 24px;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    border: 1px solid ${({ theme }) => theme.border};
    pointer-events: none;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  width: 100%;
  max-width: 240px;
  position: relative;
  padding: 0 32px;
  box-sizing: border-box;
  height: 30px;
  margin-top: -8px;
  background: transparent;
  -webkit-tap-highlight-color: transparent;
`;

const ZoomIcon = styled.div`
  color: ${({ theme }) => theme.text};
  opacity: 0.7;
  font-size: 32px;
  font-weight: normal;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  user-select: none;
  width: 32px;
  height: 32px;
  line-height: 1;
  
  ${({ position }) => position === 'left' ? `
    left: 0;
  ` : `
    right: 0;
  `}
`;

const ZoomSlider = styled.input`
  width: 100%;
  -webkit-appearance: none;
  appearance: none;
  height: 30px;
  background: transparent !important;
  outline: none !important;
  position: relative;
  margin: 0 0;
  padding: 0;
  cursor: pointer;
  border: none !important;
  -webkit-tap-highlight-color: transparent;
  
  /* 防止在移动设备上出现红色背景 */
  -webkit-tap-highlight-color: rgba(0,0,0,0) !important;
  tap-highlight-color: rgba(0,0,0,0) !important;
  
  &:focus {
    outline: none;
    box-shadow: none;
    border: none;
    background: transparent !important;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: none;
    border: none;
    background: transparent !important;
  }
  
  &::-moz-focus-outer {
    border: 0;
  }
  
  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 30px;
    cursor: pointer;
    background: transparent !important;
    border: none;
    outline: none;
    box-shadow: none;
  }
  
  &::-moz-range-track {
    width: 100%;
    height: 30px;
    cursor: pointer;
    background: transparent !important;
    border: none;
    outline: none;
    box-shadow: none;
  }
  
  /* 创建更粗的线条 */
  &::before {
    content: '';
    position: absolute;
    left: 9px;
    right: 9px;
    top: 50%;
    height: 1px;
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
    pointer-events: none;
  }
  
  /* 移除任何可能的悬停背景 */
  &:hover, &:active {
    background: transparent !important;
    outline: none !important;
    box-shadow: none !important;
    border: none !important;
  }
  
  /* 移除Chrome特有的矩形悬停框和点击效果 */
  &::-webkit-slider-thumb:hover,
  &::-webkit-slider-thumb:active,
  &::-webkit-slider-thumb:focus {
    box-shadow: none !important;
    background: #FF4D4F !important;
    outline: none !important;
    border: none !important;
  }
  
  /* 删除Chrome中滑动条激活时的红色样式 */
  &::-webkit-slider-runnable-track:active,
  &::-webkit-slider-runnable-track:focus,
  &:active,
  &:focus {
    background: transparent !important;
    outline: none !important;
    border: none !important;
    box-shadow: none !important;
  }
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #FF4D4F;
    cursor: pointer;
    transition: none;
    position: relative;
    z-index: 1;
    margin-top: 6px;
    border: none !important;
    box-shadow: none !important;
    
    &:hover, &:active, &:focus {
      transform: none;
      background: #FF4D4F !important;
      box-shadow: none !important;
      outline: none !important;
      border: none !important;
    }
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border: none !important;
    border-radius: 50%;
    background: #FF4D4F;
    cursor: pointer;
    transition: none;
    z-index: 1;
    margin-top: 6px;
    box-shadow: none !important;
    
    &:hover, &:active, &:focus {
      transform: none;
      background: #FF4D4F !important;
      box-shadow: none !important;
      outline: none !important;
      border: none !important;
    }
  }
  
  /* 覆盖webkit的auto-fill样式，这常导致意外的红色 */
  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  }
`;

const UploadButton = styled.button`
  width: 200px;
  height: 199px;
  border-radius: 50%;
  border: 2px dashed ${({ theme }) => theme.border};
  background: transparent;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.secondaryText};
  transition: all 0.3s ease;
  padding: 0;
  margin: 0;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.3)'};
    color: ${({ theme }) => theme.text};
  }

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 8px;
  }

  span {
    font-size: 14px;
    text-align: center;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  width: 100%;
`;

const Button = styled.button`
  flex: 1;
  height: 36px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${({ primary }) => primary ? `
    background: #FF4D4F;
    color: white;
    
    &:hover {
      background: #E53E3E;
    }

    &:active {
      background: #d9363e;
    }
  ` : `
    background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5'};
    color: ${({ theme }) => theme.text};
    
    &:hover {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#e0e0e0'};
    }

    &:active {
      background: ${({ theme }) => theme.isDarkMode ? 'rgba(255, 255, 255, 0.2)' : '#d9d9d9'};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageUpload = ({ currentImage, onUpload, onClose }) => {
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const fileInputRef = useRef(null);
  const editorRef = useRef(null);
  
  // 加载图片并设置crossOrigin
  useEffect(() => {
    if (currentImage && typeof currentImage === 'string') {
      setLoading(true);
      
      const loadImageWithFallbacks = async () => {
        // 尝试各种方法加载图片
        try {
          // 1. 首先尝试直接设置图片URL
          setImage(currentImage);
          setLoading(false);
          setImageLoaded(true);
        } catch (error) {
          console.error('直接加载图片失败:', error);
          
          try {
            // 2. 尝试添加时间戳避免缓存问题
            const timestamp = Date.now();
            const urlWithTimestamp = currentImage.includes('?') 
              ? `${currentImage}&t=${timestamp}` 
              : `${currentImage}?t=${timestamp}`;
              
            setImage(urlWithTimestamp);
            setLoading(false);
            setImageLoaded(true);
          } catch (error2) {
            console.error('带时间戳加载图片失败:', error2);
            
            // 3. 如果依然失败，尝试创建一个新的Image对象
            try {
              const img = new Image();
              img.crossOrigin = 'anonymous';
              
              img.onload = () => {
                // 创建canvas并转为dataURL
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                
                try {
                  const dataURL = canvas.toDataURL('image/png');
                  setImage(dataURL);
                } catch (canvasErr) {
                  // 如果无法获取dataURL，回退到原始URL
                  console.error('Canvas转换失败:', canvasErr);
                  setImage(currentImage);
                }
                setLoading(false);
                setImageLoaded(true);
              };
              
              img.onerror = () => {
                console.error('Image加载失败，回退到默认图片');
                // 完全回退 - 使用默认头像或空状态
                setImage(null);
                setLoading(false);
                setImageLoaded(false);
              };
              
              // 尝试添加随机参数避免缓存
              const cacheBuster = Math.random().toString(36).substring(7);
              img.src = currentImage.includes('?') 
                ? `${currentImage}&noCache=${cacheBuster}` 
                : `${currentImage}?noCache=${cacheBuster}`;
            } catch (error3) {
              console.error('所有方法均失败，无法加载图片:', error3);
              setImage(null);
              setLoading(false);
              setImageLoaded(false);
            }
          }
        }
      };
      
      loadImageWithFallbacks();
    }
  }, [currentImage]);

  const handleScaleChange = (e) => {
    setScale(parseFloat(e.target.value));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSave = async () => {
    if (editorRef.current && image) {
      try {
        // 使用canvas获取编辑后的图片
        const canvas = editorRef.current.getImageScaledToCanvas();
        
        // 尝试从canvas获取dataURL
        try {
          const dataURL = canvas.toDataURL('image/jpeg', 0.95);
          
          // 将DataURL转换为Blob
          const byteString = atob(dataURL.split(',')[1]);
          const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          
          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }
          
          const dataBlob = new Blob([ab], { type: mimeString });
          // 压缩图片大小确保小于1MB
          const compressedDataBlob = await compressBlob(dataBlob);
          const result = await onUpload(compressedDataBlob);
          
          if (!result.success) {
            Message.error(result.message || '保存图片时出错', 'avatar-modal-message');
          }
        } catch (err) {
          console.error('Canvas处理失败，尝试备用方法:', err);
          
          // 备用方法：使用canvas.toBlob
          canvas.toBlob(async (blob) => {
            if (blob) {
              try {
                // 压缩图片大小确保小于1MB
                const compressedBlob = await compressBlob(blob);
                const result = await onUpload(compressedBlob);
                if (!result.success) {
                  Message.error(result.message || '保存图片时出错', 'avatar-modal-message');
                }
              } catch (blobErr) {
                console.error('Blob处理失败，尝试使用原始图片:', blobErr);
                
                // 如果处理失败，直接使用原始文件
                if (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files[0]) {
                  const file = fileInputRef.current.files[0];
                  const result = await onUpload(file);
                  
                  if (!result.success) {
                    Message.error(result.message || '保存图片时出错', 'avatar-modal-message');
                  }
                } else {
                  Message.error('无法处理图片，请重新选择', 'avatar-modal-message');
                }
              }
            } else {
              Message.error('处理图片失败，请重新选择图片', 'avatar-modal-message');
            }
          }, 'image/jpeg', 0.95);
        }
      } catch (err) {
        console.error('Error processing image:', err);
        Message.error('处理图片失败，请重试', 'avatar-modal-message');
      }
    } else {
      onClose();
    }
  };

  // 压缩Blob对象，确保大小小于1MB
  const compressBlob = async (blob) => {
    if (blob.size <= 1024 * 1024) return blob; // 如果已经小于1MB，直接返回
    
    const img = document.createElement('img');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        // 初始质量
        let quality = 0.9;
        const maxSize = 1024 * 1024; // 1MB
        
        // 设置canvas尺寸，保持宽高比但减小大小
        let width = img.width;
        let height = img.height;
        
        // 如果图片太大，先按比例缩小
        const maxDimension = 1200;
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round(height * (maxDimension / width));
            width = maxDimension;
          } else {
            width = Math.round(width * (maxDimension / height));
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // 绘制到canvas
        ctx.fillStyle = '#FFF'; // 白色背景
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, width, height);
        
        // 压缩并检查大小
        const compressRecursive = (q) => {
          canvas.toBlob((resultBlob) => {
            if (!resultBlob) {
              reject(new Error('Canvas toBlob failed'));
              return;
            }
            
            if (resultBlob.size <= maxSize || q <= 0.1) {
              // 已经小于1MB或者质量已经降到最低，返回结果
              resolve(resultBlob);
            } else {
              // 继续压缩
              compressRecursive(q - 0.1);
            }
          }, 'image/jpeg', q);
        };
        
        compressRecursive(quality);
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(blob);
    });
  };
  
  // 压缩文件对象
  const compressImage = async (file) => {
    const blob = file instanceof Blob ? file : await fileToBlob(file);
    return compressBlob(blob);
  };
  
  // 将File对象转换为Blob对象
  const fileToBlob = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const blob = new Blob([reader.result], { type: file.type });
          resolve(blob);
        } catch (e) {
          reject(e);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  return (
    <Container>
      <UploadContainer>
        <EditorContainer onClick={handleUploadClick} style={{ cursor: 'pointer' }}>
          {loading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              color: '#666'
            }}>
              加载中...
            </div>
          ) : image ? (
            <AvatarEditor
              ref={editorRef}
              image={image}
              width={200}
              height={199}
              border={0}
              borderRadius={100}
              color={[0, 0, 0, 0]}
              scale={scale}
              rotate={0}
              crossOrigin="anonymous"
              onLoadFailure={(e) => {
                console.error('Avatar editor failed to load image:', e);
                // 如果头像编辑器加载图片失败，重试一次使用原始URL
                setImage(currentImage);
              }}
              disableCanvasRotation={true}
            />
          ) : (
            <UploadButton>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              <span>点击上传头像</span>
            </UploadButton>
          )}
        </EditorContainer>

        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={handleFileChange}
        />

        {image && (
          <ZoomControls>
            <ZoomIcon position="left">-</ZoomIcon>
            <ZoomSlider
              type="range"
              min={1}
              max={2}
              step={0.01}
              value={scale}
              onChange={handleScaleChange}
            />
            <ZoomIcon position="right">+</ZoomIcon>
          </ZoomControls>
        )}

        <ButtonGroup>
          <Button onClick={onClose}>取消</Button>
          <Button primary onClick={handleSave}>确定</Button>
        </ButtonGroup>
      </UploadContainer>
    </Container>
  );
};

export default ImageUpload; 